import { FEATURES } from '@/constants/features';

// Canonical, path-based URLs for the /data page, following the Tenant Admin
// pattern (Metric Studio, Hermes): the open tab, sub-tab and record live in the
// path, so links can be shared and Back/Forward move between them.
// Bare /data opens the remembered tab (see Overview_List_ETL_Utility).

// Page_Tabs label -> path segment.
export const DATA_TAB_SEGMENTS = {
  Agent: 'agent',
  Feeds: 'feeds',
  Catalog: 'catalog',
  Ingestion: 'ingestion',
  MDR: 'mdr',
  Reconciliation: 'reconciliation',
  DataSources: 'data-sources',
  DataAttributes: 'data-attributes',
  Workflows: 'workflows',
  'Workflow Builder': 'workflow-builder',
  Uploads: 'uploads',
};

// Sub-tabs inside a tab: component key -> path segment.
export const INGESTION_VIEWS = ['routines', 'schemas', 'history'];
export const MDR_TAB_SEGMENTS = { Search: 'search', Contracts: 'contracts', Feeds: 'feeds', Input: 'input' };
export const RECONCILIATION_SUBTABS = ['assignment', 'unsupported'];

const DATA_META = { canAccess: FEATURES.MODULE_DATA, requiresAuth: true, layout: 'default' };
const TAB_PATTERN = Object.values(DATA_TAB_SEGMENTS).join('|');
const MDR_PATTERN = Object.values(MDR_TAB_SEGMENTS).join('|');

export function dataTabLabel(segment) {
  return Object.entries(DATA_TAB_SEGMENTS).find(([, s]) => s === segment)?.[0] || null;
}

export function dataTabSegment(label) {
  return DATA_TAB_SEGMENTS[label] || null;
}

export function dataTabLocation(label) {
  return { name: 'DataTab', params: { tab: dataTabSegment(label) } };
}

const blank = (v) => v == null || v === '';

export function dataAttributeLocation(id) {
  if (blank(id)) return dataTabLocation('DataAttributes');
  return { name: 'DataAttribute', params: { id: String(id) } };
}

export function dataWorkflowLocation(id) {
  if (blank(id)) return dataTabLocation('Workflows');
  return { name: 'DataWorkflow', params: { id: String(id) } };
}

export function dataWorkflowBuilderLocation(id) {
  if (blank(id)) return dataTabLocation('Workflow Builder');
  return { name: 'DataWorkflowBuilder', params: { id: String(id) } };
}

// Catalog tables are addressed by SCHEMA.TABLE.
export function dataCatalogLocation(table) {
  if (!table) return dataTabLocation('Catalog');
  return { name: 'DataCatalogTable', params: { table: String(table) } };
}

// Ingestion: /data/ingestion/<view>, /data/ingestion/routines/<id|new>,
// /data/ingestion/schemas/<id>.
export function dataIngestionLocation({ view = 'routines', routineId = null, schemaId = null } = {}) {
  if (!blank(routineId)) return { name: 'DataIngestionRoutine', params: { routineId: String(routineId) } };
  if (!blank(schemaId)) return { name: 'DataIngestionSchema', params: { schemaId: String(schemaId) } };
  return { name: 'DataIngestionView', params: { view } };
}

// MDR: /data/mdr/<tab>, and a patient opened from Search or a contract roster at
// /data/mdr/<tab>/patients/<PATIENT_MASTER> (the EMPI surrogate key, never an MBI).
export function dataMdrLocation({ tab = 'Search', patient = null } = {}) {
  const segment = MDR_TAB_SEGMENTS[tab] || tab;
  if (!blank(patient)) return { name: 'DataMdrPatient', params: { mdrTab: segment, patient: String(patient) } };
  return { name: 'DataMdrTab', params: { mdrTab: segment } };
}

export function mdrTabLabel(segment) {
  return Object.entries(MDR_TAB_SEGMENTS).find(([, s]) => s === segment)?.[0] || null;
}

export function dataReconciliationLocation(subtab) {
  if (blank(subtab)) return dataTabLocation('Reconciliation');
  return { name: 'DataReconciliation', params: { subtab } };
}

// Uploads: the open folder is the rest of the path, /data/uploads/a/b/c.
export function dataUploadsLocation(segments = []) {
  const path = (segments || []).filter(Boolean);
  if (!path.length) return dataTabLocation('Uploads');
  return { name: 'DataUploads', params: { path } };
}

// Detail routes and the top-level tab each one belongs to.
const DETAIL_ROUTES = [
  ['DataAttribute', 'DataAttributes', '/data/data-attributes/:id(\\d+)'],
  ['DataCatalogTable', 'Catalog', '/data/catalog/tables/:table([^/.]+\\.[^/]+)'],
  ['DataWorkflow', 'Workflows', '/data/workflows/:id(\\d+)'],
  ['DataWorkflowBuilder', 'Workflow Builder', '/data/workflow-builder/:id(\\d+)'],
  ['DataIngestionRoutine', 'Ingestion', '/data/ingestion/routines/:routineId(new|\\d+)'],
  ['DataIngestionSchema', 'Ingestion', '/data/ingestion/schemas/:schemaId(\\d+)'],
  ['DataIngestionView', 'Ingestion', `/data/ingestion/:view(${INGESTION_VIEWS.join('|')})`],
  ['DataMdrPatient', 'MDR', `/data/mdr/:mdrTab(${MDR_PATTERN})/patients/:patient`],
  ['DataMdrTab', 'MDR', `/data/mdr/:mdrTab(${MDR_PATTERN})`],
  ['DataReconciliation', 'Reconciliation', `/data/reconciliation/:subtab(${RECONCILIATION_SUBTABS.join('|')})`],
  ['DataUploads', 'Uploads', '/data/uploads/:path+'],
];

export const DATA_DETAIL_TABS = Object.fromEntries(DETAIL_ROUTES.map(([name, tab]) => [name, tab]));

export function createDataRoutes(component) {
  return [
    { path: '/data', name: 'Data', component, meta: DATA_META },
    ...DETAIL_ROUTES.map(([name, , path]) => ({ path, name, component, meta: DATA_META })),
    { path: `/data/:tab(${TAB_PATTERN})`, name: 'DataTab', component, meta: DATA_META },
  ];
}
